import { createFileRoute, useNavigate, useRouterState } from '@tanstack/react-router';
import React from 'react';

import { AuthCard } from '@/auth/components/auth-card';
import { Heading } from '@/common/components/heading';
import { Spinner } from '@/common/components/spinner';
import { isNullOrWhitespace } from '@/common/string';
import { useAuth } from '@/shared/auth/use-auth';
import { EmptyState, EmptyStateImage, EmptyStateMessage } from '@/shared/components/empty-states';

export const Route = createFileRoute('/impersonate')({
  component: ImpersonatePage,
  head: () => ({
    meta: [
      { title: 'Impersonation | myTalquin' }
    ]
  })
});

function ImpersonatePage() {
  const router = useRouterState();
  const accessToken = router.location.hash;
  const { impersonate, user } = useAuth();
  const navigate = useNavigate();

  const hasValidAccessToken = !isNullOrWhitespace(accessToken);

  React.useEffect(() => {
    if (hasValidAccessToken && !impersonate.isPending && !impersonate.isSuccess) {
      impersonate.mutate({ accessToken });
    }
  }, [accessToken, impersonate, hasValidAccessToken]);

  React.useEffect(() => {
    if (impersonate.isSuccess && user.data) {
      void navigate({ to: '/', replace: true });
    }
  }, [impersonate.isSuccess, navigate, user.data]);

  const hasError = user.isError || impersonate.isError || !hasValidAccessToken;

  if (hasError) {
    return (
      <main className="flex flex-col md:flex-row h-full w-full p-0">
        <AuthCard>
          <EmptyState>
            <Heading>Impersonation</Heading>
            <EmptyStateImage
              src="/img/disconnected_plugs.svg"
              alt="An illustration of two plugs that have been unplugged from each other."
            />
            <EmptyStateMessage>
              The impersonation access token was invalid or has expired.
            </EmptyStateMessage>
          </EmptyState>
        </AuthCard>
      </main>
    );
  }

  return (
    <main className="flex flex-col md:flex-row h-full w-full p-0">
      <AuthCard>
        <EmptyState>
          <Heading>Impersonating</Heading>
          <Spinner color="primary" size="xl" className="my-4"/>
          <EmptyStateMessage>
            Please wait while we log you in as the member.
          </EmptyStateMessage>
        </EmptyState>
      </AuthCard>
    </main>
  );
}
